// src/utils/firebase/features/tags/writers.js
import { getDbOrThrow } from "../../core/firestoreCore";
import { COLLECTIONS, userScopedId, slugify } from "../../core/collections";
import {
  collection,
  doc,
  writeBatch,
  serverTimestamp,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { isDryRunFlag, logDryRunBanner, coerceUser } from "../../core/writerUtils";

/* ------------------------------------------------------------------ */
/* Utilities (local; avoids UI deps)                                  */
/* ------------------------------------------------------------------ */
export const TAG_PATH_SEP = " / ";

export function segmentsFromPath(path) {
  const s = String(path || "").trim();
  if (!s) return [];
  const segs = [];
  let buf = "";
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === "\\" && i + 1 < s.length) {
      const next = s[i + 1];
      if (next === "/" || next === "\\") {
        buf += next;
        i++;
        continue;
      }
    }
    if (ch === "/") {
      const token = buf.trim();
      if (token) segs.push(token);
      buf = "";
      while (i + 1 < s.length && s[i + 1] === " ") i++;
      continue;
    }
    buf += ch;
  }
  const last = buf.trim();
  if (last) segs.push(last);
  return segs;
}

export function normalizeFullPath(path) {
  const segs = segmentsFromPath(path);
  return segs.join(TAG_PATH_SEP);
}

/* ------------------------------------------------------------------ */
/* Canonical shaper: Tags                                             */
/* ------------------------------------------------------------------ */
export function shapeTag(input) {
  const rawFull = input?.fullPath || input?.name || "";
  const fullPath = normalizeFullPath(rawFull);
  if (!fullPath) return null;

  const segs = segmentsFromPath(fullPath);
  const name = segs[segs.length - 1] || fullPath;
  const parentSegs = segs.slice(0, -1);
  const parentPath = parentSegs.join(TAG_PATH_SEP);
  const ancestors = parentSegs.map((_, i) => {
    const ap = segs.slice(0, i + 1).join(TAG_PATH_SEP);
    return { name: segs[i], fullPath: ap, slug: slugify(ap) };
  });

  return {
    name,
    fullPath,
    slug: slugify(fullPath),
    parentPath,
    parentSlug: parentPath ? slugify(parentPath) : "",
    ancestors,
    depth: segs.length,
    source: input?.source || "csv-upload",
  };
}

/* ------------------------------------------------------------------ */
/* Single-record helpers (existing)                                   */
/* ------------------------------------------------------------------ */

export async function createUserTag(payload, { dryRun } = {}) {
  const db = getDbOrThrow();
  const { uid, email } = coerceUser(payload);

  const { name, fullPath: rawFullPath, source = "ui-manual", parentPath, ...rest } = payload || {};
  const DRY = isDryRunFlag(dryRun);

  let fullPath = normalizeFullPath(rawFullPath || "");
  if (!fullPath) {
    if (!name) throw new Error("createUserTag: requires name or fullPath");
    fullPath = parentPath ? normalizeFullPath(`${parentPath} / ${name}`) : normalizeFullPath(name);
  }

  const shaped = shapeTag({ name, fullPath, source });

  const id = userScopedId(uid, shaped.slug);
  const ref = doc(collection(db, COLLECTIONS.userTags), id);

  const now = serverTimestamp();
  const record = {
    name: shaped.name,
    fullPath: shaped.fullPath,
    slug: shaped.slug,
    parentPath: shaped.parentPath,
    parentSlug: shaped.parentSlug,
    ancestors: shaped.ancestors,
    depth: shaped.depth,
    children: [],
    userId: uid,
    userEmail: email,
    source,
    createdAt: now,
    updatedAt: now,
    ...rest,
  };

  if (DRY) {
    logDryRunBanner("createUserTag", { id, fullPath: record.fullPath, depth: record.depth });
  } else {
    const existing = await getDoc(ref);
    if (existing.exists()) throw new Error(`Tag "${shaped.fullPath}" already exists.`);
    await setDoc(ref, record, { merge: true });
  }

  return { id, ...record, __dryRun: DRY };
}

export async function updateUserTag({ user, userId, userEmail, id, slug, updates }, { dryRun } = {}) {
  const db = getDbOrThrow();

  let tagId = id || null;
  if (!tagId) {
    const { uid } = coerceUser({ user, userId, userEmail });
    if (!slug) throw new Error("updateUserTag: supply id or slug");
    tagId = userScopedId(uid, slug.toLowerCase());
  }
  const ref = doc(collection(db, COLLECTIONS.userTags), tagId);

  const DRY = isDryRunFlag(dryRun);

  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("updateUserTag: tag not found");
  const existing = snap.data() || {};

  let nextFullPath;
  if (updates?.fullPath) {
    nextFullPath = normalizeFullPath(updates.fullPath);
  } else if (Object.prototype.hasOwnProperty.call(updates || {}, "name")) {
    const leaf = String(updates.name || "").trim();
    if (!leaf) throw new Error("updateUserTag: name cannot be empty");
    nextFullPath = existing.parentPath
      ? normalizeFullPath(`${existing.parentPath} / ${leaf}`)
      : normalizeFullPath(leaf);
  } else {
    nextFullPath = existing.fullPath;
  }

  const shaped = shapeTag({ name: updates?.name ?? existing.name, fullPath: nextFullPath, source: updates?.source ?? existing.source });

  const patchBase = {
    name: shaped.name,
    fullPath: shaped.fullPath,
    slug: shaped.slug,
    parentPath: shaped.parentPath,
    parentSlug: shaped.parentSlug,
    ancestors: shaped.ancestors,
    depth: shaped.depth,
    ...(updates && Object.prototype.hasOwnProperty.call(updates, "source") ? { source: updates.source } : {}),
    updatedAt: serverTimestamp(),
  };
  const patch = Object.fromEntries(Object.entries(patchBase).filter(([, v]) => v !== undefined));

  if (DRY) {
    logDryRunBanner("updateUserTag", { id: tagId, keys: Object.keys(patch), fullPath: shaped.fullPath });
  } else {
    await updateDoc(ref, patch);
  }

  return true;
}

/* ------------------------------------------------------------------ */
/* Reparent / rename subtree (existing, now using shapeTag)           */
/* ------------------------------------------------------------------ */

export async function moveUserTagSubtree({
  id,
  slug,
  user,
  userId,
  userEmail,
  newParentPath,
  newLeafName,
  createMissingAncestors = true,
  dryRun,
}) {
  const db = getDbOrThrow();

  let tagId = id || null;
  let uidFromUser = null;
  let emailFromUser = null;

  if (!tagId) {
    const { uid, email } = coerceUser({ user, userId, userEmail });
    if (!slug) throw new Error("moveUserTagSubtree: supply id or slug");
    tagId = userScopedId(uid, slug.toLowerCase());
    uidFromUser = uid;
    emailFromUser = email;
  }

  const DRY = isDryRunFlag(dryRun);

  const ref = doc(collection(db, COLLECTIONS.userTags), tagId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("moveUserTagSubtree: tag not found");

  const current = snap.data();
  const oldRootPath = normalizeFullPath(current.fullPath || current.name || "");

  const uid = current.userId || uidFromUser;
  const email = current.userEmail || emailFromUser;
  if (!uid) throw new Error("moveUserTagSubtree: could not determine userId for subtree move");

  const normParent = normalizeFullPath(newParentPath || "");
  const leaf = String(newLeafName || "").trim();
  if (!leaf) throw new Error("moveUserTagSubtree: newLeafName cannot be empty");

  const newRootPath = normParent ? `${normParent}${TAG_PATH_SEP}${leaf}` : leaf;
  const normalizedNewRootPath = normalizeFullPath(newRootPath);

  if (normalizedNewRootPath === oldRootPath) {
    if (DRY) logDryRunBanner("moveUserTagSubtree (noop)", { oldRootPath, normalizedNewRootPath });
    return { moved: 0, __dryRun: DRY };
  }

  if (normParent && createMissingAncestors) {
    const segs = segmentsFromPath(normParent);
    const prefixes = [];
    for (let i = 0; i < segs.length; i++) {
      prefixes.push(segs[i]);
      const fp = prefixes.join(TAG_PATH_SEP);
      const shapedAnc = shapeTag({ fullPath: fp, name: prefixes[i], source: "ui-move-create-ancestor" });
      const ancId = userScopedId(uid, shapedAnc.slug);
      const ancRef = doc(collection(db, COLLECTIONS.userTags), ancId);
      const ancSnap = await getDoc(ancRef);
      if (!ancSnap.exists()) {
        const toCreate = {
          name: shapedAnc.name,
          fullPath: shapedAnc.fullPath,
          slug: shapedAnc.slug,
          parentPath: shapedAnc.parentPath,
          parentSlug: shapedAnc.parentSlug,
          ancestors: shapedAnc.ancestors,
          depth: shapedAnc.depth,
          children: [],
          userId: uid,
          userEmail: email || null,
          source: shapedAnc.source,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        if (DRY) {
          logDryRunBanner("create missing ancestor", { id: ancId, fullPath: toCreate.fullPath });
        } else {
          await setDoc(ancRef, toCreate, { merge: true });
        }
      }
    }
  }

  const start = oldRootPath;
  const end = `${oldRootPath}\uf8ff`;
  const qy = query(
    collection(db, COLLECTIONS.userTags),
    where("userId", "==", uid),
    orderBy("fullPath"),
    where("fullPath", ">=", start),
    where("fullPath", "<", end)
  );
  const qSnap = await getDocs(qy);
  if (qSnap.empty) {
    if (DRY) logDryRunBanner("moveUserTagSubtree (empty)", { oldRootPath });
    return { moved: 0, __dryRun: DRY };
  }

  const updates = [];
  for (const d of qSnap.docs) {
    const data = d.data();
    const oldPath = String(data.fullPath || "").trim();

    let rel = "";
    if (oldPath.length > oldRootPath.length) {
      rel = oldPath.slice(oldRootPath.length).replace(/^\s*\/\s*/, "");
    }
    const nextFullPath = rel ? `${normalizedNewRootPath}${TAG_PATH_SEP}${rel}` : normalizedNewRootPath;
    const shaped = shapeTag({ fullPath: nextFullPath, source: data.source || "ui-move" });

    updates.push({
      id: d.id,
      patch: {
        name: shaped.name,
        fullPath: shaped.fullPath,
        slug: shaped.slug,
        parentPath: shaped.parentPath,
        parentSlug: shaped.parentSlug,
        ancestors: shaped.ancestors,
        depth: shaped.depth,
        updatedAt: serverTimestamp(),
      },
    });
  }

  if (DRY) {
    logDryRunBanner("moveUserTagSubtree", { from: oldRootPath, to: normalizedNewRootPath, count: updates.length });
    return { moved: updates.length, __dryRun: DRY };
  }

  const BATCH = 400;
  for (let i = 0; i < updates.length; i += BATCH) {
    const b = writeBatch(db);
    for (const u of updates.slice(i, i + BATCH)) {
      const r = doc(collection(db, COLLECTIONS.userTags), u.id);
      b.update(r, u.patch);
    }
    await b.commit();
  }

  return { moved: updates.length, __dryRun: DRY };
}
